// Activity (Workspace 2): who did what, and when, in plain words — every entry of the engine's audit log the cloud keeps (paged,
// newest first, "Show older" and scrolling for more — no silent cap) with the engagement's messages, and an "Agent / system" view:
// the agent runs (role card, task, commands, refusals, how each ended), the engine's own activity (what it published and pulled,
// the UI's requests it ran, the daily archive, errors) with its heartbeat, and the engine's refusals. Older days are archived
// locally to a workbook (ADR-0032); the page says so. The page follows the engine live and says when it last published.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { Chips, EngineDetails, useWs } from "./common";
import { Button, ErrorState, LoadingRows, StatusPill } from "../../components";
import { useLiveTables } from "../../data/live";
import type { Activity, AgentRun, Heartbeat, Refusal, SystemEvent } from "../../data/types";
import type { DataSource } from "../../data/source";
import { ago, fmtStamp } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { LiveStamp } from "./LiveStamp";

const VERB: Record<string, string> = {
  "engagement.create": "created the engagement", "team.add": "added a team member", "vault.add": "added a client document", "tb.import": "imported the trial balance",
  "materiality.set": "set materiality", "risk.assess": "assessed a risk", "workpaper.open": "opened a workpaper", "workpaper.answer": "filled in a workpaper value",
  "workpaper.decide": "decided on a proposed value", "workpaper.render": "rendered a workpaper", "procedure.do": "performed a procedure step", "procedure.accept": "accepted a procedure step",
  "evidence.read": "read a value from evidence", "evidence.verify": "checked a value from evidence", "review.note": "left a review note", "review.respond": "answered a review note",
  "review.clear": "cleared a review note", "signoff.record": "signed off a workpaper", "pbc.request": "asked the client for a document", "pbc.send": "sent a client request",
  "pbc.receive": "received a client document", "letter.sign": "recorded the signed engagement letter", "acceptance.decide": "decided acceptance", "attest.record": "recorded an attestation",
  "wp.message": "posted a message", "wp.answer": "answered a question", "wp.accept": "accepted an answer", "agent.request": "gave the agent an instruction",
  "run.start": "started an agent run", "run.end": "ended an agent run", "cloud.pull": "brought in what the client sent through the portal",
};
/** A plain sentence for an act; an unlisted act says which part of the file it touched. */
export const actText = (a: Pick<Activity, "command">) => VERB[a.command] ?? `updated the ${a.command.split(".")[0].replace(/_/g, " ")} record`;

export const PAGE_SIZE = 100;

/** One page of the log through the source's paging, or — a source without it — from the whole published table. */
export async function activityPage(ds: DataSource, id: string, before: number | null, limit = PAGE_SIZE): Promise<{ rows: Activity[]; more: boolean }> {
  if (ds.activityPage) return ds.activityPage(id, before, limit);
  const all = (await ds.table(id, "activity")).sort((a, b) => b.seq - a.seq).filter((a) => before === null || a.seq < before);
  return { rows: all.slice(0, limit), more: all.length > limit };
}

/** The log, page by page: the newest page first, older pages on request, new entries merged in when the engine publishes. */
export function useActivityLog(ds: DataSource, id: string) {
  const [st, setSt] = useState<{ rows: Activity[]; more: boolean; loading: boolean; error?: string }>({ rows: [], more: false, loading: true });
  const busy = useRef(false);
  useEffect(() => {
    let live = true;
    setSt({ rows: [], more: false, loading: true });
    activityPage(ds, id, null).then((p) => live && setSt({ rows: p.rows, more: p.more, loading: false }), (e: Error) => live && setSt({ rows: [], more: false, loading: false, error: e.message }));
    return () => { live = false; };
  }, [ds, id]);
  const older = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setSt((s) => ({ ...s, loading: true }));
    try {
      const last = st.rows[st.rows.length - 1]?.seq ?? null;
      const p = await activityPage(ds, id, last);
      setSt((s) => ({ rows: [...s.rows, ...p.rows.filter((r) => !s.rows.some((x) => x.seq === r.seq))], more: p.more, loading: false }));
    } catch (e) { setSt((s) => ({ ...s, loading: false, error: (e as Error).message })); }
    finally { busy.current = false; }
  }, [ds, id, st.rows]);
  const newest = useCallback(async () => {
    try {
      const p = await activityPage(ds, id, null);
      setSt((s) => {
        const top = s.rows[0]?.seq ?? 0;
        const fresh = p.rows.filter((r) => r.seq > top);
        if (fresh.length === p.rows.length && s.rows.length) return { rows: p.rows, more: p.more, loading: false };   // a gap: start over
        const kept = s.rows.filter((r) => !fresh.some((x) => x.seq === r.seq));
        return { ...s, rows: [...fresh, ...kept], more: s.rows.length ? s.more : p.more, loading: false };
      });
    } catch { /* the next signal tries again */ }
  }, [ds, id]);
  return { ...st, older, newest };
}

type View = "all" | "people" | "agent" | "system";

export function WsActivity() {
  const ws = useWs();
  const { ds } = useApp();
  const log = useActivityLog(ds, ws.id);
  const runs = useLoad(() => ds.table(ws.id, "agent_runs"), [ds, ws.id]);
  const refusals = useLoad(() => ds.table(ws.id, "refusals").catch(() => [] as Refusal[]), [ds, ws.id]);
  const system = useLoad(() => ds.table(ws.id, "system_events").catch(() => [] as SystemEvent[]), [ds, ws.id]);
  const beat = useLoad(() => ds.heartbeat().catch(() => [] as Heartbeat[]), [ds]);
  const live = useLiveTables(ds, ws.id, ["activity", "agent_runs", "refusals", "system_events"], () => {
    void log.newest(); runs.reload(); refusals.reload(); system.reload(); beat.reload(); ws.reload();
  });
  const [view, setView] = useState<View>("all");
  const retention = (system.data ?? []).find((e) => e.retention)?.retention ?? null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div><h1 className="text-xl font-semibold">Activity</h1><p className="text-sm text-muted">Everything done on this engagement, newest first.</p></div>
        <LiveStamp live={live} />
      </header>
      <Chips label="Show" value={view} onChange={setView}
        options={[{ id: "all", label: "Everyone" }, { id: "people", label: "People" }, { id: "agent", label: "Agent" }, { id: "system", label: "Agent / system" }]} />
      {view === "system"
        ? <SystemView runs={runs.data} refusals={refusals.data} events={system.data} beats={beat.data} error={runs.error} />
        : <LogView view={view} log={log} retention={retention} />}
      <EngineDetails>
        <p>The audit log as the engine records it (command, sequence number, hash): <Link className="text-brand hover:underline" to={`/e/${ws.id}/details`}>engine details</Link>.</p>
      </EngineDetails>
    </div>
  );
}

function LogView({ view, log, retention }: { view: View; log: ReturnType<typeof useActivityLog>; retention: SystemEvent["retention"] }) {
  const ws = useWs();
  const sentinel = useRef<HTMLLIElement | null>(null);
  const oldest = log.rows[log.rows.length - 1]?.at ?? null;
  const rows = [
    ...log.rows.map((a) => ({ key: `a${a.seq}`, at: a.at, actor: a.actor, agent: !!a.agent, text: actText(a), detail: a })),
    ...ws.messages.filter((m) => !log.more || !oldest || m.createdAt >= oldest).map((m) => ({ key: `m${m.id}`, at: m.createdAt, actor: m.author === "agent" ? "Agent" : m.author, agent: m.author === "agent",
      text: `${m.kind === "question" ? "asked" : m.kind === "answer" ? "answered" : m.kind === "instruction" ? "told the agent" : "wrote"}: “${m.body.length > 90 ? m.body.slice(0, 90) + "…" : m.body}”`, detail: null as Activity | null })),
  ].filter((r) => view === "all" || (view === "agent") === r.agent).sort((a, b) => b.at.localeCompare(a.at));
  const { more, loading, older } = log;
  useEffect(() => {                                                   // scrolling to the end loads the next page
    const el = sentinel.current;
    if (!el || !more || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting) && !loading) void older(); });
    io.observe(el);
    return () => io.disconnect();
  }, [more, loading, older]);
  if (log.error) return <ErrorState error={log.error} onRetry={() => void log.newest()} />;
  if (log.loading && !log.rows.length) return <LoadingRows rows={6} label="Loading activity" />;
  return (
    <div className="space-y-2">
      <ol className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised" aria-label="Activity">
        {rows.map((r) => (
          <li key={r.key} className="flex flex-wrap items-baseline gap-x-2 px-4 py-2 text-sm">
            <span className={`font-medium ${r.agent ? "text-agent" : ""}`}>{r.agent && <span aria-hidden="true">✦ </span>}{r.actor}</span>
            <span className="min-w-0 flex-1">{r.text}{r.detail?.agent && !r.agent ? <span className="text-xs text-agent"> (drafted by an agent)</span> : null}</span>
            <time className="text-xs text-muted" dateTime={r.at}>{fmtStamp(r.at)}</time>
          </li>
        ))}
        {rows.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted">No activity yet.</li>}
        {more && <li ref={sentinel} aria-hidden="true" className="h-px" />}
      </ol>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted" aria-label="Activity paging">
        <span>{log.rows.length} log entr{log.rows.length === 1 ? "y" : "ies"} shown{more ? " — older entries follow" : ""}</span>
        {more && <Button size="sm" kind="secondary" disabled={loading} onClick={() => void older()}>{loading ? "Loading…" : "Show older"}</Button>}
        {!more && <ArchiveNote retention={retention} />}
      </div>
    </div>
  );
}

/** What the cloud keeps (the retention row): older days live in the daily workbooks on the engine's machine. */
export function ArchiveNote({ retention }: { retention: SystemEvent["retention"] }) {
  if (!retention) return <span>That is every entry published.</span>;
  const last = retention.lastArchive;
  if (retention.floorDay) return <span>Older activity (through {retention.floorDay}) is archived locally{last ? ` — last archive ${last.day}` : ""}; the cloud keeps {retention.retentionDays} days.</span>;
  return <span>That is every entry{last ? `; archived locally through ${retention.archivedThrough ?? last.day}` : ""}.</span>;
}

const KIND: Record<string, string> = { push: "Published to the app", pull: "Brought in from the portal", inbox: "Ran a request from the app", archive: "Daily activity archive", error: "Error" };

function SystemView({ runs, refusals, events, beats, error }: { runs?: AgentRun[]; refusals?: Refusal[]; events?: SystemEvent[]; beats?: Heartbeat[]; error?: string }) {
  if (error) return <ErrorState error={error} />;
  if (!runs || !events || !refusals) return <LoadingRows rows={6} label="Loading the agent and system activity" />;
  const sorted = [...runs].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const sys = events.filter((e) => !e.retention).sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
  const retention = events.find((e) => e.retention)?.retention ?? null;
  const hb = (beats ?? []).sort((a, b) => b.lastSeen.localeCompare(a.lastSeen))[0];
  const online = hb ? Date.now() - new Date(hb.lastSeen).getTime() < 90_000 : false;
  return (
    <div className="space-y-4">
      <section aria-label="Engine" className="rounded-lg border border-line bg-raised px-4 py-3 text-sm">
        {hb ? (
          <p className="flex flex-wrap items-center gap-2">
            <StatusPill kind={online ? "done" : "blocked"} label={online ? "Engine online" : "Engine offline"} />
            <span className="text-muted">{hb.holderName}'s engine · heartbeat {ago(hb.lastSeen)} · last publish {ago(hb.lastPush)} · last pull {ago(hb.lastPull)}
              {hb.agentWatch ? ` · agent watcher ${hb.agentWatch.running ? "running" : "stopped"}` : ""}</span>
          </p>
        ) : <p className="text-muted">No engine heartbeat is published.</p>}
        <p className="mt-1 text-xs text-muted"><ArchiveNote retention={retention} /></p>
      </section>
      <section aria-labelledby="sys-runs" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h2 id="sys-runs" className="border-b border-line px-4 py-2 text-sm font-semibold">Agent runs · {sorted.length}</h2>
        {sorted.length === 0 ? <p className="px-4 py-4 text-sm text-muted">No agent has worked on this engagement.</p> : (
          <ul className="divide-y divide-line" aria-label="Agent runs">
            {sorted.map((r) => (
              <li key={r.id} className="space-y-1 px-4 py-2 text-sm">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-agent"><span aria-hidden="true">✦ </span>{r.agent}</span>
                  <span className="text-xs text-muted">role card {r.roleCard}{r.person ? ` · for ${r.person}` : ""}</span>
                  <span className="flex-1" />
                  <StatusPill state={r.endedAt ? (r.outcome === "done" ? "done" : "blocked") : r.state === "paused" ? "waiting" : "in_progress"}
                    label={r.endedAt ? `Ended${r.outcome ? ` — ${r.outcome}` : ""}` : r.state === "paused" ? "Paused" : "Working"} />
                </div>
                {r.task && <p className="text-sm">{r.task}</p>}
                <p className="text-xs text-muted">started {fmtStamp(r.startedAt)}{r.endedAt ? ` · ended ${fmtStamp(r.endedAt)}` : ""} · {r.commandCount} command{r.commandCount === 1 ? "" : "s"}
                  {r.refusals.length ? ` · ${r.refusals.length} refused` : ""}</p>
                {r.summary && <p className="text-xs text-muted">{r.summary}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="sys-events" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h2 id="sys-events" className="border-b border-line px-4 py-2 text-sm font-semibold">System activity · {sys.length}</h2>
        {sys.length === 0 ? <p className="px-4 py-4 text-sm text-muted">Nothing published yet (the engine journals each publish, pull and daily archive).</p> : (
          <ol className="divide-y divide-line" aria-label="System activity">
            {sys.map((e) => (
              <li key={e.key} className="flex flex-wrap items-baseline gap-x-2 px-4 py-2 text-sm">
                <span className="font-medium">{KIND[e.kind] ?? e.kind}</span>
                <span className="min-w-0 flex-1">{e.summary}{e.kind === "archive" && e.path ? <span className="block break-all text-xs text-muted">{e.path}</span> : null}</span>
                {!e.ok && <StatusPill kind="blocked" label="Failed" />}
                <time className="text-xs text-muted" dateTime={e.at ?? undefined}>{fmtStamp(e.at)}</time>
              </li>
            ))}
          </ol>
        )}
      </section>
      <section aria-labelledby="sys-refusals" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h2 id="sys-refusals" className="border-b border-line px-4 py-2 text-sm font-semibold">Refused by the engine · {refusals.length}</h2>
        {refusals.length === 0 ? <p className="px-4 py-4 text-sm text-muted">Nothing was refused.</p> : (
          <ol className="divide-y divide-line" aria-label="Refusals">
            {[...refusals].sort((a, b) => b.at.localeCompare(a.at)).map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-x-2 px-4 py-2 text-sm">
                <span className={`font-medium ${r.agent ? "text-agent" : ""}`}>{r.agent ? `✦ ${r.agent}` : r.actor}</span>
                <span className="min-w-0 flex-1">{r.message}</span>
                <time className="text-xs text-muted" dateTime={r.at}>{fmtStamp(r.at)}</time>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
